import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PlusIcon, MenuTrashIcon, TimePicker } from '@noor/ui';
import { groupDigits, stripToDigits } from '@noor/lib';
import type { MenuProduct } from '@/entities/menu';
import { toPickedProduct } from '../lib/branch-products';
import type { BranchProduct } from '../model/schema';
import { ProductPicker } from './product-picker';

interface ProductStepProps {
  rows: BranchProduct[];
  onChange: (rows: BranchProduct[]) => void;
}

/**
 * Step 2 of the branch wizard — «Добавление продукта» (Figma 1067:12534).
 *
 * One row per attached product: the name in a wide read-only pill, the
 * branch-local price and optional count in narrow editable pills, and a
 * trailing bin that detaches it. Below them a dashed «Добавить продукт» opens
 * the picker.
 *
 * The rows are NOT a `FormField` array. They are not part of the branch's own
 * record — they write to the Product Service, on a different id, through
 * different RPCs — so they live as plain state owned by the wizard and are
 * submitted by `useSyncBranchProducts` rather than by the branch form.
 *
 * The NAME is read-only: it belongs to the shared product, and editing it here
 * would rename it in every branch. Only the overlay fields are branch-local,
 * so only price and count are editable.
 */
export function ProductStep({ rows, onChange }: ProductStepProps) {
  const { t } = useTranslation('eats');
  // Mounted only while open, so each open seeds from the current rows rather
  // than a draft the merchant abandoned last time.
  const [isPickerOpen, setPickerOpen] = useState(false);

  function handleConfirm(products: MenuProduct[]): void {
    const picked = new Map(products.map((product) => [String(product.id), product]));
    // Rows the merchant kept ticked survive AS THEY ARE — re-committing the
    // picker must not reset a price that was already typed, nor drop the
    // `attached` flag the submit diff depends on.
    const kept = rows.filter((row) => picked.has(row.productId));
    const keptIds = new Set(kept.map((row) => row.productId));
    const added = products
      .filter((product) => !keptIds.has(String(product.id)))
      .map(toPickedProduct);

    onChange([...kept, ...added]);
    setPickerOpen(false);
  }

  function handlePrice(productId: string, raw: string): void {
    const digits = stripToDigits(raw);
    onChange(
      rows.map((row) =>
        row.productId === productId
          ? { ...row, price: digits === '' ? null : Number(digits) }
          : row,
      ),
    );
  }

  function handleCount(productId: string, raw: string): void {
    const digits = stripToDigits(raw);
    onChange(
      rows.map((row) =>
        row.productId === productId
          ? { ...row, stockQuantity: digits === '' ? null : Number(digits) }
          : row,
      ),
    );
  }

  function handleHours(
    productId: string,
    side: 'availableFrom' | 'availableTo',
    value: string,
  ): void {
    onChange(
      rows.map((row) => {
        if (row.productId !== productId) return row;
        // A cleared picker drops the key rather than storing '' — the row then
        // reads as "inherits the dish's hours" again.
        const rest = { ...row };
        delete rest[side];
        return value === '' ? rest : { ...rest, [side]: value };
      }),
    );
  }

  function handleRemove(productId: string): void {
    onChange(rows.filter((row) => row.productId !== productId));
  }

  return (
    <div className="flex flex-col gap-4">
      {rows.length > 0 && (
        <ul className="flex flex-col gap-4">
          {rows.map((row) => (
            <li key={row.productId} className="flex flex-col gap-2">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_3rem] items-center gap-3 sm:flex">
                {/* The product's name, shown as a field but not editable — see
                 * the note above. `aria-readonly` rather than `disabled` so it
                 * stays readable to a screen reader. */}
                <span className="col-span-3 flex h-11 min-w-0 items-center rounded-3xl border border-input bg-card px-4 text-sm font-normal tracking-tight text-foreground shadow-xs sm:flex-1">
                  <span className="truncate">{row.name}</span>
                </span>

                {/* The price pill carries the «сум» unit the design shows, and
                 * every other money box in the panel has — without it the
                 * merchant cannot tell what currency the number is read in. The
                 * unit sits INSIDE the pill, so the box is the wrapper and the
                 * input is transparent within it. */}
                <label className="flex h-11 min-w-0 items-center gap-1 rounded-3xl border border-input bg-card px-4 shadow-xs focus-within:border-input-border-focus sm:w-[11.3125rem] sm:shrink-0">
                  <span className="sr-only">
                    {t('branches.form.products.priceLabel', { name: row.name })}
                  </span>
                  <input
                    inputMode="numeric"
                    value={row.price === null ? '' : groupDigits(String(row.price))}
                    onChange={(event) => handlePrice(row.productId, event.target.value)}
                    placeholder={t('branches.form.products.pricePlaceholder')}
                    className="min-w-0 flex-1 bg-transparent text-sm font-medium tracking-tight text-foreground outline-none placeholder:text-muted-foreground"
                  />
                  {row.price !== null && (
                    <span className="shrink-0 text-sm font-medium tracking-tight text-foreground">
                      {t('branches.form.currencySuffix')}
                    </span>
                  )}
                </label>

                <label className="flex h-11 min-w-0 items-center gap-1 rounded-3xl border border-input bg-card px-4 shadow-xs focus-within:border-input-border-focus sm:w-32 sm:shrink-0">
                  <span className="sr-only">
                    {t('branches.form.products.countLabel', { name: row.name })}
                  </span>
                  <input
                    inputMode="numeric"
                    value={
                      row.stockQuantity == null
                        ? ''
                        : groupDigits(String(row.stockQuantity))
                    }
                    onChange={(event) => handleCount(row.productId, event.target.value)}
                    placeholder={t('branches.form.products.countPlaceholder')}
                    className="min-w-0 flex-1 bg-transparent text-sm font-medium tracking-tight text-foreground outline-none placeholder:text-muted-foreground"
                  />
                </label>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('branches.form.products.remove', { name: row.name })}
                  onClick={() => handleRemove(row.productId)}
                  className="size-12 shrink-0 rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <MenuTrashIcon className="size-6" />
                </Button>
              </div>

              {/* The branch's own selling hours. Both empty = it inherits the
               * dish's hours, which the placeholder says rather than showing a
               * time the branch never chose. */}
              <div className="flex min-w-0 flex-nowrap items-center gap-2 pl-1 text-sm text-muted-foreground">
                <span className="shrink-0 text-xs font-medium whitespace-nowrap">
                  {t('branches.form.products.hoursLabel')}
                </span>
                <TimePicker
                  value={row.availableFrom}
                  onChange={(value) => handleHours(row.productId, 'availableFrom', value)}
                  placeholder={t('branches.form.products.hoursInherit')}
                  aria-label={t('branches.form.products.hoursFrom', { name: row.name })}
                  className="h-9 w-36 shrink-0"
                />
                <span aria-hidden className="shrink-0">
                  –
                </span>
                <TimePicker
                  value={row.availableTo}
                  onChange={(value) => handleHours(row.productId, 'availableTo', value)}
                  placeholder={t('branches.form.products.hoursInherit')}
                  aria-label={t('branches.form.products.hoursTo', { name: row.name })}
                  allowEndOfDay
                  className="h-9 w-36 shrink-0"
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Dashed outline capsule — the design's one "add another" affordance. */}
      <Button
        type="button"
        variant="ghost"
        onClick={() => setPickerOpen(true)}
        className="h-12 w-fit gap-2 rounded-full border-2 border-dashed border-input px-4 text-base font-semibold text-foreground"
      >
        <PlusIcon className="size-[18px]" />
        {t('branches.form.products.add')}
      </Button>

      {isPickerOpen && (
        <ProductPicker
          open={isPickerOpen}
          onOpenChange={setPickerOpen}
          selectedIds={rows.map((row) => row.productId)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
